/* eslint-disable */
import type { Connection, MemcmpFilter } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import BN from "bn.js";
import type { Schema } from "borsh";
import { deserialize } from "borsh";

/**
 * 64-bit value
 */
export class u64 extends BN {
  /**
   * Convert to Buffer representation
   */
  toBuffer(endian?: BN.Endianness | undefined, length?: number | undefined): Buffer {
    const a = super.toArray().reverse();
    const b = Buffer.from(a);
    if (b.length === 8) {
      return b;
    }
    if (b.length > 8) {
      throw new Error("u64 too large");
    }

    const zeroPad = Buffer.alloc(8);
    b.copy(zeroPad);
    return zeroPad;
  }

  /**
   * Construct a u64 from Buffer representation
   */
  static fromBuffer(buffer: Buffer): u64 {
    if (buffer.length !== 8) {
      throw new Error(`Invalid buffer length: ${buffer.length}`);
    }
    return new u64(
      [...buffer]
        .reverse()
        .map((i) => `00${i.toString(16)}`.slice(-2))
        .join(""),
      16,
    );
  }
}

const CENTRAL_BUFF_LEN = 365 * 3;

export enum Tag {
  Uninitialized = 0,
  StakeAccount = 1,
  CentralState = 2,
  Closed = 3,
}

export class StakeAccount {
  tag: Tag;
  nonce: number;
  owner: PublicKey;
  depositTime: BN;
  stakeAmount: BN;
  stakingPeriod: BN;
  rewardsClaimed: BN;
  lastClaimedTime: BN;

  static schema: Schema = new Map([
    [
      StakeAccount,
      {
        kind: "struct",
        fields: [
          ["tag", "u8"],
          ["nonce", "u8"],
          ["owner", [32]],
          ["depositTime", "u64"],
          ["stakeAmount", "u64"],
          ["stakingPeriod", "u64"],
          ["rewardsClaimed", "u64"],
          ["lastClaimedTime", "u64"],
        ],
      },
    ],
  ]);

  constructor(obj: {
    tag: number;
    nonce: number;
    owner: Uint8Array;
    depositTime: BN;
    stakeAmount: BN;
    stakingPeriod: BN;
    rewardsClaimed: BN;
    lastClaimedTime: BN;
  }) {
    this.tag = obj.tag as Tag;
    this.nonce = obj.nonce;
    this.owner = new PublicKey(obj.owner);
    this.depositTime = obj.depositTime;
    this.stakeAmount = obj.stakeAmount;
    this.stakingPeriod = obj.stakingPeriod;
    this.rewardsClaimed = obj.rewardsClaimed;
    this.lastClaimedTime = obj.lastClaimedTime;
  }

  /**
   * Derives the stake account key and nonce
   * @param owner The owner of the stake account
   * @param stakePeriod The stake period for which the tokens are staked
   * @param stakAmount The amount of tokens to stkae
   * @param programId The staking program ID
   * @returns a Promise<[PublicKey, number]> object
   */
  static async getKey(
    owner: PublicKey,
    stakePeriod: number,
    stakAmount: number,
    presentTime: number,
    programId: PublicKey,
  ) {
    const seeds = [
      owner.toBuffer(),
      new u64(stakePeriod).toBuffer(),
      new u64(stakAmount).toBuffer(),
      new u64(presentTime).toBuffer(),
    ];
    return await PublicKey.findProgramAddress(seeds, programId);
  }

  /**
   *  Deserializes a buffer into a StakeAccount object
   * @param data Buffer to deserialize
   * @returns a StakeAccount object
   */
  static deserialize(data: Buffer) {
    return deserialize(this.schema, StakeAccount, data);
  }

  /**
   * Retrieves and deserializes a stake account
   * @param connection The solana connection object to the RPC node
   * @param address The address of the stake account
   * @returns a StakeAccount object
   */
  static async retrieve(connection: Connection, address: PublicKey) {
    const accountInfo = await connection.getAccountInfo(address);
    if (!accountInfo || !accountInfo.data) {
      throw new Error("Stake account not found");
    }
    return this.deserialize(accountInfo.data);
  }

  /**
   * Retrieves all the stake account for a user
   * @param connection The solana connection object to the RPC node
   * @param owner The owner of the stake accounts
   * @returns an array of StakeAccount objects
   */
  static async retrieveStakeAccountsForOwner(connection: Connection, owner: PublicKey, programId: PublicKey) {
    const filters: MemcmpFilter[] = [
      {
        memcmp: {
          offset: 2,
          bytes: owner.toBase58(),
        },
      },
      {
        memcmp: {
          offset: 0,
          bytes: "2",
        },
      },
    ];

    const result = await connection.getProgramAccounts(programId, { filters });

    return result.map((e) => {
      return {
        address: e.pubkey,
        stakeAccount: this.deserialize(e.account.data),
      };
    });
  }

  /**
   * Returns the lock time for the rewards of the stake account
   */
  calcRewardLockTime() {
    return (365 * (365 * 3 - this.stakingPeriod.toNumber())) / (365 * 3 - 7);
  }
}

export class CentralState {
  tag: Tag;
  nonce: number;

  currentDayIdx: number;
  stakeAccountsCounter: number;
  lastCrankTime: BN;
  lastVaultBalance: BN;
  vault: PublicKey;
  totalStaked: BN;
  mint: PublicKey;
  govMint: PublicKey;

  // Metrics
  totalFeesDistributed: BN;

  leftover: BN;

  rewards: BN[];

  static schema: Schema = new Map([
    [
      CentralState,
      {
        kind: "struct",
        fields: [
          ["tag", "u8"],
          ["nonce", "u8"],
          ["currentDayIdx", "u16"],
          ["stakeAccountsCounter", "u32"],
          ["lastCrankTime", "u64"],
          ["lastVaultBalance", "u64"],
          ["vault", [32]],
          ["totalStaked", "u64"],
          ["mint", [32]],
          ["govMint", [32]],
          ["totalFeesDistributed", "u64"],
          ["rewards", ["u64", CENTRAL_BUFF_LEN]],
        ],
      },
    ],
  ]);

  constructor(obj: {
    tag: number;
    nonce: number;
    stakeAccountsCounter: number;
    lastCrankTime: BN;
    lastVaultBalance: BN;
    vault: Uint8Array;
    totalStaked: BN;
    mint: Uint8Array;
    govMint: Uint8Array;
    currentDayIdx: number;
    totalFeesDistributed: BN;
    totalFeesAccumulated: BN;
    leftover: BN;
    rewards: BN[];
  }) {
    this.tag = obj.tag as Tag;
    this.nonce = obj.nonce;
    this.stakeAccountsCounter = obj.stakeAccountsCounter;
    this.lastCrankTime = obj.lastCrankTime;
    this.lastVaultBalance = obj.lastVaultBalance;
    this.vault = new PublicKey(obj.vault);
    this.totalStaked = obj.totalStaked;
    this.mint = new PublicKey(obj.mint);
    this.govMint = new PublicKey(obj.govMint);
    this.currentDayIdx = obj.currentDayIdx;
    this.totalFeesDistributed = obj.totalFeesDistributed;
    this.leftover = obj.leftover;
    this.rewards = obj.rewards;
  }

  /**
   * Deserializes a buffer into a CentralState object
   * @param data Buffer to deserialize
   * @returns a CentralState object
   */
  static deserialize(data: Buffer) {
    return deserialize(this.schema, CentralState, data);
  }

  /**
   * Retrieves and deserializes the central state
   * @param connection The solana connection object to the RPC node
   * @param address The address of the central state
   * @returns a CentralState object
   */
  static async retrieve(connection: Connection, address: PublicKey) {
    const accountInfo = await connection.getAccountInfo(address);
    if (!accountInfo || !accountInfo.data) {
      throw new Error("Central state not found");
    }
    return this.deserialize(accountInfo.data);
  }

  /**
   * Derives the central state key and nonce
   * @param programId The staking program ID
   * @returns a Promise<[PublicKey, number]> object
   */
  static async getKey(programId: PublicKey) {
    return await PublicKey.findProgramAddress([programId.toBuffer()], programId);
  }
}
